import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useDocumentStore } from '../storage/index.ts';

const DOCUMENTS_KEY = ['documents'] as const;

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function DocumentListPage() {
  const store = useDocumentStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const documents = useQuery({ queryKey: DOCUMENTS_KEY, queryFn: () => store.list() });

  const create = useMutation({
    mutationFn: () => store.create(),
    onSuccess: (document) => {
      void queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEY });
      void navigate(`/d/${document.id}`);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => store.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEY }),
  });

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-8">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-neutral-900">Burfy Docs</h1>
        <button
          type="button"
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          disabled={create.isPending}
          onClick={() => create.mutate()}
        >
          New document
        </button>
      </header>

      {documents.isPending && <p className="text-sm text-neutral-500">Loading…</p>}

      {documents.isError && (
        <p className="text-sm text-red-700" role="alert">
          Your documents could not be opened. Reload the page to try again.
        </p>
      )}

      {documents.data?.length === 0 && (
        <p className="text-sm text-neutral-500">
          No documents yet. Create one to get started.
        </p>
      )}

      {documents.data && documents.data.length > 0 && (
        <ul className="divide-y divide-neutral-200 rounded-md border border-neutral-200">
          {documents.data.map((document) => (
            <li key={document.id} className="flex items-center gap-4 px-4 py-3">
              <Link
                to={`/d/${document.id}`}
                className="flex-1 truncate text-sm font-medium text-neutral-900 hover:underline"
              >
                {document.title || 'Untitled document'}
              </Link>
              <span className="shrink-0 text-xs text-neutral-500">
                {formatDate(document.updatedAt)}
              </span>
              <button
                type="button"
                className="shrink-0 rounded px-2 py-1 text-xs text-neutral-500 hover:bg-neutral-100 hover:text-red-700"
                aria-label={`Delete ${document.title || 'Untitled document'}`}
                onClick={() => remove.mutate(document.id)}
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
